import type { UseQueryResult } from '@tanstack/react-query';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { getQueryData, isUnhandledErrorQuery } from '@repo/api/endpoints/merchant';
import { ProviderType, providerType } from '@repo/shared/types';
import type { LimitMerchant, PreLimit } from '@repo/shared/types';
import { isRequestPending, isApiError, isApiValidationError422 } from '@repo/api';
import type { ResponseError } from '@repo/api';
import {
  isUserLimitOrAccountantType,
  isUserNotPrivilegedToTakeLoan,
  isUserActiveInP24,
  hasUserZeroPreLimit,
  areP24MarketingDataAccepted,
  hasUserEqualOrGreaterThan3000AvailableLimit,
  hasUserNoAvailableLimit,
  hasUserLoans,
  hasUserActiveLoan,
  hasUserLessThan3000Prelimit,
  hasMerchantLiberisOffers,
  isPragmaPromoted,
  isLiberisPromoted,
  hasUserLessThan3000AvailableLimit,
  areAllProviderNotPromoted,
  isOnlyPragmaAvailable,
  shouldMerchantFillLiberisOfferData,
  isAvailableMultiProvider,
  providerAvailablePreLimitOrLimitValues,
} from '@repo/store/helpers';

import { VIEW_TYPE } from './view.type';
import type { ViewType } from './view.type';

/**
 * Limit (przyznany) ma pole `available`, PreLimit jeszcze nie. Type-guard, żeby
 * bezpiecznie odczytać dostępną kwotę z wyniku
 * `providerAvailablePreLimitOrLimitValues` (zwraca `LimitMerchant | PreLimit`).
 */
const hasAvailableLimit = (value: LimitMerchant | PreLimit | undefined): value is LimitMerchant =>
  !!value && 'available' in value;

export const setCurrentViewForMerchantHashResponse = ({
  merchantResponseData,
  omitDashboard,
  currentProvider,
}: {
  merchantResponseData: UseQueryResult<MerchantResponse, ResponseError>;
  omitDashboard?: boolean;
  currentProvider?: ProviderType;
}): ViewType => {
  const merchantQueryData = getQueryData(merchantResponseData);

  // LOADING
  if (isRequestPending(merchantResponseData.status)) {
    return VIEW_TYPE.APP_LOADING;
  }

  const isMerchant422 =
    merchantResponseData.error !== null && isApiValidationError422(merchantResponseData.error);

  if (
    !isMerchant422 &&
    (isApiError(merchantResponseData.error) ||
      merchantQueryData === 'RESPONSE_422_VALIDATION_ERROR')
  ) {
    return VIEW_TYPE.SERVICE_NOT_AVAILABLE;
  }
  if (isUnhandledErrorQuery(merchantResponseData)) {
    return VIEW_TYPE.SERVICE_NOT_AVAILABLE;
  }
  if (
    merchantQueryData &&
    merchantQueryData !== 'RESPONSE_422_VALIDATION_ERROR' &&
    currentProvider
  ) {
    const userHaveLoans = hasUserLoans(merchantQueryData);

    const userCannotTakeLoan =
      isUserLimitOrAccountantType(merchantQueryData) ||
      isUserNotPrivilegedToTakeLoan(merchantQueryData);
    const areOffersAvailable = merchantQueryData.data.offers?.length > 0;

    /**
     * Priorytet widoków: aktywna pożyczka > oferty > pożyczki nieaktywne.
     * Dashboard pokazujemy więc tylko wtedy, gdy jest aktywna pożyczka albo
     * gdy nie ma żadnych ofert do wyboru.
     */
    const isDashboardViewEnable =
      userHaveLoans &&
      !omitDashboard &&
      (hasUserActiveLoan(merchantQueryData) || !areOffersAvailable);

    const pragmaLimitOrPreLimitValues = providerAvailablePreLimitOrLimitValues(
      merchantQueryData,
      providerType.enum.pragma,
    );

    const liberisLimitOrPreLimitValues = providerAvailablePreLimitOrLimitValues(
      merchantQueryData,
      providerType.enum.liberis,
    );

    // Limit ma `available`, PreLimit (limit jeszcze nieprzyznany) ma `max`.
    const pragmaAvailableAmount = hasAvailableLimit(pragmaLimitOrPreLimitValues)
      ? pragmaLimitOrPreLimitValues.available
      : (pragmaLimitOrPreLimitValues?.max ?? 0);
    const isPragmaLoanFormEligible =
      pragmaAvailableAmount >= 3000 &&
      (isOnlyPragmaAvailable(merchantQueryData) ||
        isPragmaPromoted(merchantQueryData) ||
        (areAllProviderNotPromoted(merchantQueryData) &&
          !shouldMerchantFillLiberisOfferData(merchantQueryData)));

    const isLiberisOfferFillRequired =
      isAvailableMultiProvider(merchantQueryData) &&
      !isLiberisPromoted(merchantQueryData) &&
      shouldMerchantFillLiberisOfferData(merchantQueryData);

    const areLiberisOffersAvailable = hasMerchantLiberisOffers(merchantQueryData);

    if (
      !userHaveLoans &&
      !pragmaLimitOrPreLimitValues &&
      !liberisLimitOrPreLimitValues &&
      !areLiberisOffersAvailable
    ) {
      return VIEW_TYPE.SERVICE_NOT_AVAILABLE;
    }

    if (!isUserActiveInP24(merchantQueryData)) {
      return VIEW_TYPE.USER_HAS_NO_ACTIVE_ACCOUNT;
    } else if (userCannotTakeLoan) {
      if (!userHaveLoans) {
        return VIEW_TYPE.LOAN_APPLICATION_LOWER_PRIVILEGE_LEVEL;
      } else if (isDashboardViewEnable) {
        return VIEW_TYPE.DASHBOARD;
      }
    } else if (isDashboardViewEnable) {
      return VIEW_TYPE.DASHBOARD;
    } else if (
      hasUserZeroPreLimit(merchantQueryData, currentProvider) &&
      hasUserNoAvailableLimit(merchantQueryData, currentProvider)
    ) {
      return VIEW_TYPE.USER_HAS_0_PRELIMIT;
    } else if (
      hasUserLessThan3000Prelimit(merchantQueryData, currentProvider) &&
      hasUserNoAvailableLimit(merchantQueryData, currentProvider)
    ) {
      return VIEW_TYPE.USER_HAS_LESS_THAN_3000_PRELIMIT;
    } else if (isDashboardViewEnable) {
      return VIEW_TYPE.DASHBOARD;
    } else if (
      (isPragmaPromoted(merchantQueryData) &&
        hasUserLessThan3000AvailableLimit(merchantQueryData, 'pragma') &&
        hasMerchantLiberisOffers(merchantQueryData)) ||
      (!isPragmaPromoted(merchantQueryData) && hasMerchantLiberisOffers(merchantQueryData))
    ) {
      return VIEW_TYPE.SELECT_OFFER_VIEW;
    } else if (
      areP24MarketingDataAccepted(merchantQueryData.data.agreements?.marketing) &&
      hasUserEqualOrGreaterThan3000AvailableLimit(merchantQueryData, currentProvider) &&
      !hasUserLessThan3000Prelimit(merchantQueryData, currentProvider)
    ) {
      return VIEW_TYPE.LOAN_FORM_VIEW;
    } else if (areP24MarketingDataAccepted(merchantQueryData.data.agreements?.marketing)) {
      if (isPragmaLoanFormEligible) {
        return VIEW_TYPE.LOAN_FORM_VIEW;
      }
      if (isLiberisOfferFillRequired) {
        return VIEW_TYPE.SELECT_OFFER_VIEW;
      }

      return VIEW_TYPE.LOAN_APPLICATION_POSSIBLE_WITH_SELECTED_MARKETING_DATA;
    } else if (!areP24MarketingDataAccepted(merchantQueryData.data.agreements?.marketing)) {
      if (isPragmaLoanFormEligible) {
        return VIEW_TYPE.LOAN_FORM_VIEW;
      }

      if (isLiberisOfferFillRequired) {
        return VIEW_TYPE.SELECT_OFFER_VIEW;
      }

      return VIEW_TYPE.LOAN_APPLICATION_POSSIBLE_WITHOUT_SELECTED_MARKETING_DATA;
    }

    return VIEW_TYPE.SERVICE_NOT_AVAILABLE;
  }

  return VIEW_TYPE.APP_LOADING;
};
